import {
  categoryLabel,
  CATEGORY_PAGE_MIN_PRODUCTS,
  isProductCategory,
} from "@listou/shared/categories";
import { catalogueStickerUrl } from "@listou/shared/stickers";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { GiftRanking, GuideShelves, ListCallout } from "../components/gift-guide";
import { Sticker } from "../components/list-page/parts";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { CATEGORY_INTROS } from "../lib/category-copy";
import { GIFT_GUIDE_PAGES } from "../lib/gift-guides";
import { seo } from "../lib/head";
import { catalogueCategoriesQuery, catalogueQuery } from "../lib/queries";
import { viewerQuery } from "../lib/session";

const guide = GIFT_GUIDE_PAGES.birth;

/** Whole sentences of the intro, as many as fit the ~160 characters a result snippet shows. */
const metaDescription = (intro: string) => {
  const [first = intro, ...rest] = intro.split(/(?<=[.!?])\s+/);
  let kept = first;
  for (const sentence of rest) {
    if (`${kept} ${sentence}`.length > 160) break;
    kept = `${kept} ${sentence}`;
  }
  return kept;
};

export const Route = createFileRoute("/idees-cadeaux-naissance/$category")({
  staticData: { ssr: true },
  loader: async ({ context: { queryClient }, params }) => {
    if (!isProductCategory(params.category)) throw notFound();
    const category = params.category;
    const [counts] = await Promise.all([
      queryClient.query({ ...catalogueCategoriesQuery("birth"), staleTime: "static" }),
      queryClient.prefetchInfiniteQuery(catalogueQuery({ occasion: "birth", category })),
    ]);
    const count = counts.categories.find((entry) => entry.category === category)?.count ?? 0;
    // A shelf with nothing for this occasion is not a page, not an empty one.
    if (count === 0) throw notFound();
    return { category, count };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { category, count } = loaderData;
    return seo({
      title: `${categoryLabel(category)} : idées cadeaux ${guide.suffix} · Listou`,
      description: metaDescription(CATEGORY_INTROS[category]),
      // A handful of products is thin content: reachable, not indexed.
      noindex: count < CATEGORY_PAGE_MIN_PRODUCTS,
    });
  },
  component: GuideCategory,
});

function GuideCategory() {
  const { category } = Route.useLoaderData();
  const viewer = useQuery({ ...viewerQuery, retry: false });
  const catalogue = useInfiniteQuery(catalogueQuery({ occasion: "birth", category }));
  const categories = useQuery(catalogueCategoriesQuery("birth"));
  return (
    <div className="min-h-dvh">
      <SiteHeader signedIn={viewer.isSuccess} />
      <main className="mx-auto grid max-w-5xl gap-8 px-4 pt-6 pb-20 sm:px-6">
        <nav aria-label="Fil d'Ariane" className="text-muted">
          <Link to="/idees-cadeaux" className="font-bold hover:underline">
            Idées cadeaux
          </Link>
          <span aria-hidden> › </span>
          <Link to={guide.hub} className="font-bold hover:underline">
            Naissance
          </Link>
        </nav>
        <header className="grid items-center gap-6 sm:grid-cols-[1fr_auto]">
          <div className="grid max-w-3xl gap-3">
            <h1 className="text-4xl font-medium sm:text-5xl">
              {categoryLabel(category)} : idées cadeaux {guide.suffix}
            </h1>
            <p className="text-xl text-ink/80">{CATEGORY_INTROS[category]}</p>
          </div>
          <Sticker
            src={catalogueStickerUrl(category)}
            className="row-start-1 w-28 rotate-6 sm:col-start-2 sm:w-40"
          />
        </header>
        <GiftRanking catalogue={catalogue} />
        <ListCallout />
        {categories.data && (
          <section className="grid gap-4 pt-4">
            <h2 className="text-3xl font-semibold">Les autres rayons</h2>
            <GuideShelves
              guide={guide}
              categories={categories.data.categories}
              current={category}
            />
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
